import { useEffect, useState, useContext, useRef } from "react";
import { UserContext } from "../../../../../../context/UserContext";
import { FlowHandlersContext } from "../../../../Menu/FlowContainer";
import { options } from "../util";
import { Stack, Typography } from "@mui/material";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import CheckboxField from "../../../../../../components/CheckboxField";
import employerFlowStyles from "../../styles";

const SINGLE_EMPLOYMENT_GROUP_MESSAGE =
  "Please select at least one employee group to proceed";

export default function ExcludedGroups(props) {
  const { getEmployerPlan, getPermissibleClass } = useContext(UserContext);
  const flowHandlers = useContext(FlowHandlersContext);
  const { mode, exitUrl, step, formik, displayOnly, permissibleClassId } =
    props;

  const permissibleClass = getPermissibleClass(permissibleClassId);
  const employerPlanId = permissibleClass?.employerPlanId;
  const employerPlan = getEmployerPlan(employerPlanId);
  const planType = employerPlan?.type || "";
  const header = "Excluded Groups";
  const description =
    "Select which employee groups to exclude from the QSEHRA. All employees are eligible and must be offered the QSEHRA unless they are in an excluded group.";

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const shouldShowErrors = hasAttemptedSubmit;

  const allOptions = options.excludedClasses["QSEHRA"];
  const firstOptionValue = allOptions[0]?.value;

  // These three options are mutually exclusive
  const mutuallyExclusiveValues = [
    allOptions[1]?.value,
    allOptions[2]?.value,
    allOptions[3]?.value,
  ].filter(Boolean);

  // Tracks the previous excludedClasses value so we can detect which key was newly toggled.
  // We can't rely on formik.values here because CheckboxField updates formik before onSelect fires.
  const previousValueRef = useRef(formik.values?.excludedClasses || {});

  useEffect(() => {
    const employeeGroups = formik?.values?.excludedClasses || {};
    const hasSelection = Object.keys(employeeGroups).length > 0;
    const nextexcludedClassesValue = hasSelection ? "yes" : "";
    if (
      (formik.values?.excludedClasses_required || "") !==
      nextexcludedClassesValue
    ) {
      formik.setFieldValue(
        "excludedClasses_required",
        nextexcludedClassesValue,
      );
    }

    let nextGroupError = "";
    if (!hasSelection) {
      nextGroupError = SINGLE_EMPLOYMENT_GROUP_MESSAGE;
    }
    if ((formik.values?.group_error || "") !== nextGroupError) {
      formik.setFieldValue("group_error", nextGroupError);
    }
  }, [formik.values?.excludedClasses, planType]);

  function buildTouchedFromErrors(errors) {
    if (!errors || typeof errors !== "object") {
      return true;
    }

    if (Array.isArray(errors)) {
      return errors.map((item) => buildTouchedFromErrors(item));
    }

    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = buildTouchedFromErrors(errors[key]);
      return acc;
    }, {});
  }

  async function handleNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setHasAttemptedSubmit(true);

    const errors = await formik.validateForm();
    const hasErrors = Object.keys(errors).length > 0;
    const employeeGroups = formik.values?.excludedClasses || {};
    const hasSelection = Object.keys(employeeGroups).length > 0;
    const noSelection = !hasSelection;

    if (noSelection) {
      if (!formik.values?.group_error) {
        formik.setFieldValue("group_error", SINGLE_EMPLOYMENT_GROUP_MESSAGE);
      }
    }

    if (hasErrors || noSelection) {
      formik.setTouched(buildTouchedFromErrors(errors), true);
      return false;
    }

    await formik.submitForm();

    if (flowHandlers?.onNext) {
      await flowHandlers.onNext(formik.values);
    }

    return false;
  }

  const isChecked = (value) => {
    return !!formik.values?.excludedClasses?.[value];
  };

  // Disable the other mutually-exclusive options when one is selected.
  // The first option (Include all employees) is not disabled — clicking another
  // option simply un-checks it via the onSelect handler below.
  const selectedExclusive = mutuallyExclusiveValues.find((v) => isChecked(v));

  const processedOptions = allOptions.map((option) => {
    let optionDisabled = false;
    if (
      selectedExclusive &&
      mutuallyExclusiveValues.includes(option.value) &&
      option.value !== selectedExclusive
    ) {
      optionDisabled = true;
    }
    return { ...option, disabled: optionDisabled };
  });

  // Bridge between CheckboxField's internal toggle and our exclusivity logic.
  // CheckboxField calls onSelect after writing its own value to formik. We then
  // detect which option was newly toggled and re-write formik to enforce rules.
  const onCheckboxSelect = ({ value }) => {
    const previous = previousValueRef.current;
    previousValueRef.current = value;

    // Find which key was newly turned on
    let newlyChecked = null;
    for (const key of Object.keys(value)) {
      if (value[key] && !previous[key]) {
        newlyChecked = key;
        break;
      }
    }

    if (!newlyChecked) {
      /**
       * An uncheck. No exclusivity rules apply, but CheckboxField leaves
       * the key behind as false rather than removing it — and a false key
       * is saved as a predefined class with isExcluded = 0, so a group the
       * user turned OFF comes back as an explicit "included" row. Drop it
       * so the group simply isn't there.
       */
      const cleaned = Object.fromEntries(
        Object.entries(value).filter(([, checked]) => checked),
      );
      previousValueRef.current = cleaned;
      formik.setFieldValue("excludedClasses", cleaned);
      return;
    }
    let newSelections = { ...value };

    if (newlyChecked === firstOptionValue) {
      // First option ("Include all") clears everything else
      newSelections = { [firstOptionValue]: true };
    } else {
      // Any other option — remove first option if it was selected
      delete newSelections[firstOptionValue];

      // If newly checked is mutually exclusive, remove other exclusive ones
      if (mutuallyExclusiveValues.includes(newlyChecked)) {
        mutuallyExclusiveValues.forEach((exclusiveValue) => {
          if (exclusiveValue !== newlyChecked) {
            delete newSelections[exclusiveValue];
          }
        });
      }
    }

    // Clean up any keys that are explicitly false
    Object.keys(newSelections).forEach((key) => {
      if (!newSelections[key]) delete newSelections[key];
    });

    // Update ref to reflect the corrected state we're about to write
    previousValueRef.current = newSelections;

    formik.setFieldValue("excludedClasses", newSelections);
  };

  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          {header}
        </Typography>
        <Typography sx={{ marginBottom: "8px" }}>{description}</Typography>

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          <CheckboxField
            formik={formik}
            type="checkbox"
            name="excludedClasses"
            options={processedOptions}
            displayKey="display"
            valueKey="value"
            disabled={displayOnly}
            onSelect={onCheckboxSelect}
            shouldShowErrors={shouldShowErrors}
          />

          {shouldShowErrors && formik.values?.group_error && (
            <div className="error-message-fields">
              {formik.values.group_error}
            </div>
          )}
        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        enableSave={true}
        exitUrl={exitUrl}
        step={step}
        onNextStep={handleNext}
      />
    </Stack>
  );
}
